"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { isLoggedIn } from "@/app/funcs/api";
import { getQuizExpiryMs, formatRemainingTime } from "../helpers/quizExpiry";
import { normalizeQuizFlag } from "../helpers/quizBooleans";
import { hasPassedQuiz } from "../helpers/quizPassing";
import { useEffect, useState } from "react";
import { getAttemptsCount } from "../helpers/quizApi";

const QuizCard = ({ quiz, ramadan }) => {
  const router = useRouter();
  const [remainingMs, setRemainingMs] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isPassed, setIsPassed] = useState(false);
  const [isAttemptsLoading, setIsAttemptsLoading] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const isRamadanQuiz = normalizeQuizFlag(ramadan ?? quiz?.ramadan);

  useEffect(() => {
    if (typeof window === 'undefined' || !quiz?.id) {
      setIsUserLoggedIn(false);
      setIsAttemptsLoading(false);
      setIsPassed(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setIsUserLoggedIn(false);
      setIsAttemptsLoading(false);
      setIsPassed(false);
      return;
    }

    let userId = null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload?.data?.user?.id || payload?.sub || payload?.user_id || null;
    } catch {
      setIsUserLoggedIn(false);
      setIsAttemptsLoading(false);
      setIsPassed(false);
      return;
    }

    if (!userId) {
      setIsUserLoggedIn(false);
      setIsAttemptsLoading(false);
      setIsPassed(false);
      return;
    }

    setIsUserLoggedIn(true);
    setIsPassed(false);
    setIsAttemptsLoading(true);
    getAttemptsCount(quiz.id, userId)
      .then((data) => {
        if (hasPassedQuiz(data?.highestGrade, isRamadanQuiz)) setIsPassed(true);
      })
      .catch(() => { })
      .finally(() => setIsAttemptsLoading(false));
  }, [quiz?.id, isRamadanQuiz]);

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  const href = `${isRamadanQuiz ? "/ramadan/" : "/services/quizzes"}/${quiz.id}`;

  useEffect(() => {
    const expireMs = getQuizExpiryMs(quiz);
    if (expireMs == null) {
      setRemainingMs(null);
      setIsExpired(false);
      return;
    }

    const update = () => {
      const diff = expireMs - Date.now();
      if (diff <= 0) {
        setRemainingMs(0);
        setIsExpired(true);
      } else {
        setRemainingMs(diff);
        setIsExpired(false);
      }
    };

    update();
    const interval = setInterval(() => {
      const diff = expireMs - Date.now();
      if (diff <= 0) {
        setRemainingMs(0);
        setIsExpired(true);
        clearInterval(interval);
      } else {
        setRemainingMs(diff);
        setIsExpired(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [quiz?.expirationAt, quiz?.ramadan, quiz?.createdAt]);

  const finalHref = isExpired ? "#" : href;

  const handleOpenQuiz = async (e) => {
    if (isExpired) {
      e.preventDefault();
      toast.info("انتهت فترة الإجابة عن السؤال", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    e.preventDefault();

    const token =
      typeof window !== "undefined" ? window.localStorage.getItem("token") : null;

    if (!token) {
      toast.error("لم تقم بتسجيل الدخول. يرجى تسجيل الدخول لبدء الاختبار.", {
        position: "top-center",
        autoClose: 4000,
      });
      return;
    }

    try {
      const ok = await isLoggedIn();
      if (!ok) {
        toast.error("لم تقم بتسجيل الدخول. يرجى تسجيل الدخول لبدء الاختبار.", {
          position: "top-center",
          autoClose: 4000,
        });
        return;
      }
    } catch {
      toast.error("تعذر التحقق من تسجيل الدخول. حاول مرة أخرى.", {
        position: "top-center",
        autoClose: 4000,
      });
      return;
    }

    router.push(href);
  };

  return (
    <Link
      href={finalHref}
      className="text-decoration-none"
      style={{ display: "block", height: "100%" }}
      onClick={handleOpenQuiz}
      aria-disabled={isExpired}
      tabIndex={isExpired ? -1 : 0}
    >
      <div
        className="card border-0 shadow-sm h-100 position-relative"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          cursor: isExpired ? "not-allowed" : "pointer",
          overflow: "hidden",
          filter: isExpired ? "grayscale(0.5)" : "none",
          opacity: isExpired ? 0.75 : 1,
        }}
        onMouseEnter={(e) => {
          if (isExpired) return;
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
        }}
        onMouseLeave={(e) => {
          if (isExpired) return;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
        }}
      >
        {/* Popular Badge */}
        {quiz.isPopular && (
          <span
            className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded-pill text-white small fw-bold"
            style={{
              background: "linear-gradient(135deg, #FF6B6B 0%, #FF8C00 100%)",
              fontSize: "0.75rem",
              zIndex: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            شائع
          </span>
        )}

        {/* Icon Section */}
        <div className="d-flex flex-column align-items-center justify-content-center p-4 bg-dark" style={{ minHeight: "180px" }}>
         { isUserLoggedIn ? (
         <>
         {isAttemptsLoading ? (
            <div className="spinner-border text-light" style={{ width: "3rem", height: "3rem" }} role="status" aria-live="polite">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <i className={`bi ${isPassed ? 'bi-check-circle text-success' :  (isExpired ? 'bi-x-circle text-danger' : quiz.icon)}`} style={{ fontSize: "4rem" }}></i>
          )}
          </>
) : (
  <i className={`bi ${quiz.icon} text-success'`} style={{ fontSize: "4rem" }}></i>
)}
           {!isExpired && remainingMs !== null && remainingMs > 0 && (
              <span
                className="badge p-2 mx-auto fs-6"
              >
                {formatRemainingTime(remainingMs)}
              </span>
            )}
        </div>

        {/* Card Body */}
        <div className="card-body p-4">
          {/* Category Label */}
          <div className="mb-2">
            <span
              className="badge bg-light text-dark border"
              style={{
                fontSize: "0.7rem",
                fontWeight: "600",
                letterSpacing: "0.5px",
              }}
            >
              {isRamadanQuiz ? "فوازير رمضان" : "اختبار"}
            </span>

            {/* {!isExpired && remainingMs !== null && remainingMs > 0 && (
              <span
                className="badge bg-success p-2 ms-2"
             
              >
                {formatRemainingTime(remainingMs)}
              </span>
            )} */}

            {isExpired && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem" }}>
                انتهت
              </span>
            )}
          </div>

          {/* Title */}
          <h5 className="fw-bold mb-2">{quiz.title}</h5>

          {/* Description */}
          <p
            className="text-muted mb-3"
            style={{
              fontSize: "0.875rem",
              lineHeight: "1.6",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: "2.8rem",
            }}
          >
            {quiz.description}
          </p>

          {/* Stats */}
          {!isRamadanQuiz && (
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-1">
                <i className="bi bi-clock text-muted" style={{ fontSize: "0.875rem" }}></i>
                <span className="text-muted small">{quiz.duration}د</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <i className="bi bi-file-earmark-text text-muted" style={{ fontSize: "0.875rem" }}></i>
                <span className="text-muted small">{ quiz.questions > 20 ? 20 : quiz.questions} سؤال</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <i className="bi bi-people text-muted" style={{ fontSize: "0.875rem" }}></i>
                <span className="text-muted small">{formatNumber(quiz.participants)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default QuizCard;